<template>
  <div class="my-4">
    <div class="flex justify-left items-center">
      <h2 class="text-xl font-bold">Invite Link Views</h2>
      <MinimalButton
        class="ml-2"
        @click="showStats = !showStats"
      >
        <FontAwesomeIcon
          :icon="faAngleDown"
          :rotation="showStats ? 180 : 0"
          class="h-4 w-4 text-blue-500"
        />
      </MinimalButton>
    </div>

    <div v-if="showStats">
      <div class="flex justify-left items-center mt-2">
        <MinimalButton
          @click="statRange = 7"
          class="mx-2"
          :class="statRange === 7 ? 'bg-blue-200' : ''"
        >
          7 Days
        </MinimalButton>

        <MinimalButton
          @click="statRange = 30"
          class="mx-2"
          :class="statRange === 30 ? 'bg-blue-200' : ''"
        >
          30 Days
        </MinimalButton>

        <MinimalButton
          @click="statRange = 180"
          class="mx-2"
          :class="statRange === 180 ? 'bg-blue-200' : ''"
        >
          6 Months
        </MinimalButton>
      </div>

      <ViewsLineChart :chartData="combinedChartData" />

      <p class="text-xs text-gray-400 mt-1">
        Unique visitors identified by hashed IP. Hover over page loads to see per-link breakdown.
      </p>
    </div>
  </div>
</template>

<script setup>
import ViewsLineChart from './ViewsLineChart.vue';
import MinimalButton from '../MinimalButton.vue';
import { defineProps, computed, ref } from 'vue';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';

const showStats = ref(false);
const statRange = ref(30);

const props = defineProps({
  views: {
    type: Array,
    required: true,
  },
});

const parsedViews = computed(() =>
  props.views.map(v => ({
    date: DateTime.fromISO(v.created_at),
    ip_hash: v.ip_hash,
    token: v.token,
  })).sort((a, b) => a.date.toMillis() - b.date.toMillis())
);

function buildDayBuckets() {
  const now = DateTime.now();
  const start = now.minus({ days: statRange.value });
  const days = {};

  for (let i = 0; i <= statRange.value; i++) {
    const key = start.plus({ days: i }).toLocaleString(DateTime.DATE_SHORT);
    days[key] = { total: 0, uniqueIps: new Set(), tokenCounts: {}, tokenUniqueIps: {} };
  }

  parsedViews.value
    .filter(({ date }) => date > now.minus({ days: statRange.value }))
    .forEach(({ date, ip_hash, token }) => {
      const key = date.toLocaleString(DateTime.DATE_SHORT);
      if (days[key]) {
        days[key].total++;
        days[key].uniqueIps.add(ip_hash);
        days[key].tokenCounts[token] = (days[key].tokenCounts[token] || 0) + 1;
        if (!days[key].tokenUniqueIps[token]) days[key].tokenUniqueIps[token] = new Set();
        days[key].tokenUniqueIps[token].add(ip_hash);
      }
    });

  return days;
}

const combinedChartData = computed(() => {
  const days = buildDayBuckets();
  const labels = Object.keys(days);
  const dayValues = Object.values(days);

  return {
    labels,
    datasets: [
      {
        label: 'Page Loads',
        data: dayValues.map(d => d.total),
        dataLabels: dayValues.map(d => {
          const entries = Object.entries(d.tokenCounts);
          if (!entries.length) return null;
          return entries.map(([token, count]) => `…${token}: ${count} load${count !== 1 ? 's' : ''}`).join('\n');
        }),
        backgroundColor: 'rgba(99, 102, 241, 0.2)',
        borderColor: 'rgba(99, 102, 241, 1)',
        borderWidth: 1,
      },
      {
        label: 'Unique Visitors',
        data: dayValues.map(d => d.uniqueIps.size),
        dataLabels: dayValues.map(d => {
          const entries = Object.entries(d.tokenUniqueIps);
          if (!entries.length) return null;
          return entries.map(([token, ips]) => `…${token}: ${ips.size} unique`).join('\n');
        }),
        backgroundColor: 'rgba(16, 185, 129, 0.2)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 1,
      },
    ],
  };
});
</script>
